import { Suspense } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, IconShield, SkeletonRows, ToastHost } from '../components';
import { api, clearOperatorToken } from '../lib/api';
import { signOut } from '../lib/auth';
import { queryKeys } from '../lib/query';
import { useSession } from '../lib/session';
import { AgentSwitcher } from './agent-switcher';
import { CLIENT_NAV } from './nav';
import styles from './shell.module.css';

/**
 * The client console shell: sidebar with all sixteen sections, agent switcher,
 * signed-in footer, operator-mode banner and the toast host.
 *
 * Nav items are filtered by capability, so a viewer sees a shorter sidebar
 * rather than a wall of links that 403 on click.
 */
export function ClientShell() {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Badge on the Approvals item. Pending work is the one thing the shell itself
  // is allowed to know about — it is the reason people open the console.
  const pendingApprovals = useQuery({
    queryKey: queryKeys.approvals('pending'),
    queryFn: ({ signal }) => api.listApprovals({ status: 'pending' }, signal),
    enabled: session.can('approvals.read'),
    refetchInterval: 30_000,
  });

  const pendingCount = pendingApprovals.data?.length ?? 0;
  const operator = session.operator;

  const exitOperatorMode = () => {
    clearOperatorToken();
    void queryClient.invalidateQueries({ queryKey: queryKeys.me() });
    navigate('/master');
  };

  return (
    <div className={styles.shell}>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      <aside className={styles.sidebar} aria-label="Sections">
        <NavLink to="/" className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            iQ
          </span>
          <span className={styles.brandText}>
            <span className={styles.brandName}>Otto IQ</span>
            <span className={styles.brandTenant}>
              {session.tenant?.name ?? (session.isLoading ? 'Loading…' : 'No organisation')}
            </span>
          </span>
        </NavLink>

        <AgentSwitcher />

        <nav className={styles.nav} aria-label="Client console">
          {CLIENT_NAV.map((group) => {
            const visible = group.items.filter(
              (item) => !item.capability || session.can(item.capability),
            );
            if (visible.length === 0) return null;

            return (
              <div className={styles.navGroup} key={group.label}>
                <span className={styles.navGroupLabel}>{group.label}</span>
                {visible.map((item) => {
                  const Icon = item.icon;
                  if (item.disabled) {
                    // A span, not a disabled link: there is no such thing as a
                    // disabled anchor, and one that still navigates on Enter is
                    // worse than none. `aria-disabled` plus no `href` keeps it
                    // in the reading order and out of the tab order.
                    return (
                      <span
                        key={item.to}
                        className={[styles.navLink, styles.navLinkDisabled].join(' ')}
                        aria-disabled="true"
                        title={item.disabledReason}
                      >
                        <Icon className={styles.navIcon} size={18} />
                        <span className={styles.navLabel}>{item.label}</span>
                        <span className={styles.navPending}>off</span>
                      </span>
                    );
                  }
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        [styles.navLink, isActive ? styles.navLinkActive : null]
                          .filter(Boolean)
                          .join(' ')
                      }
                    >
                      <Icon className={styles.navIcon} size={18} />
                      <span className={styles.navLabel}>{item.label}</span>
                      {item.to === '/approvals' && pendingCount > 0 ? (
                        <span className={styles.navCount} aria-label={`${pendingCount} pending`}>
                          {pendingCount}
                        </span>
                      ) : null}
                      {item.pending ? (
                        <span className={styles.navPending} title="Not built yet">
                          soon
                        </span>
                      ) : null}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className={styles.footer}>
          <div className={styles.footerPerson}>
            <Avatar name={session.person?.name ?? 'Signed out'} kind="person" size="md" />
            <span className={styles.footerText}>
              <span className={styles.footerName}>
                {session.person?.name ?? (session.isLoading ? 'Signing in…' : 'Not signed in')}
              </span>
              <span className={styles.footerMeta}>
                {session.isLoading
                  ? '—'
                  : [session.tenant?.name, session.person?.email, session.role]
                      .filter(Boolean)
                      .join(' · ')}
              </span>
            </span>
          </div>
          {/*
            No link to the master console. The client build does not contain a
            `/master` route at all — `routes.tsx` mounts it only for the master
            surface — and deployed they are separate origins with separate Entra
            app registrations. Staff reach a client tenant through operator mode,
            which lives inside the master bundle.
          */}
          <div className={styles.footerLinks}>
            <Button variant="ghost" size="sm" onClick={signOut}>
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <div className={styles.main}>
        {operator ? (
          <div className={styles.operatorBanner} role="status">
            <IconShield size={18} />
            <span className={styles.operatorBannerText}>
              Operator mode — you are inside {session.tenant?.name ?? 'a client'}&rsquo;s console.
              <span className={styles.operatorBannerDetail}>
                Everything you do here is written to this client&rsquo;s audit log
                {operator.operatorName ? ` as ${operator.operatorName}` : ''}
                {operator.expiresAt ? `. Session ends ${operator.expiresAt}` : ''}.
              </span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={exitOperatorMode}
              style={{ color: 'inherit', flexShrink: 0 }}
            >
              Exit operator mode
            </Button>
          </div>
        ) : null}

        <main className={styles.content} id="main-content" tabIndex={-1}>
          <Suspense fallback={<SkeletonRows rows={5} />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <ToastHost />
    </div>
  );
}

export default ClientShell;
